import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { User } from 'firebase/auth';
import { auth } from '../services/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import CustomerStageAnalytics from '../components/analytics/CustomerStageAnalytics';
import BusinessAnalytics from '../components/analytics/BusinessAnalytics';
import ConversionFunnelAnalytics from '../components/analytics/ConversionFunnelAnalytics';
import BehaviorInsights from '../components/analytics/BehaviorInsights';
import CustomersTab from '../components/admin/CustomersTab';
import SettingsTab from '../components/admin/SettingsTab';
import { SponsoredSlotsTab } from '../components/admin';
import FeedbackTab from '../components/admin/FeedbackTab';
import EmailLogsTab from '../components/admin/EmailLogsTab';
import { isAdmin as checkIsAdmin } from '../config/adminConfig';
import './AdminPage.css';

type AdminTab = 'analytics' | 'customers' | 'sponsored' | 'feedback' | 'emailLogs' | 'settings';
type AnalyticsSubTab = 'dashboard' | 'engagement' | 'conversion' | 'affiliate' | 'retention';
type Language = 'en' | 'ko' | 'pt-BR' | 'es';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('customers');
  const [analyticsSubTab, setAnalyticsSubTab] = useState<AnalyticsSubTab>('dashboard');
  const [analyticsDateRange, setAnalyticsDateRange] = useState<'7days' | '30days' | '90days' | 'year'>('30days');
  const [language] = useState<Language>(() => {
    // Detect language from localStorage or browser
    const saved = localStorage.getItem('language') as Language | null;
    return saved || (navigator.language.startsWith('ko') ? 'ko' : 'en');
  });

  // Track scroll state for tab bar fade indicators
  const tabsRef = useRef<HTMLElement>(null);
  const [tabsScrollClass, setTabsScrollClass] = useState('');
  const updateTabsScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const atStart = el.scrollLeft > 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setTabsScrollClass(
      [atStart ? 'scrolled-start' : '', atEnd ? 'scrolled-end' : ''].filter(Boolean).join(' ')
    );
  }, []);

  useEffect(() => {
    document.title = 'Admin — Heseda';
    updateTabsScroll();

    // Update fade indicators when viewport resizes (e.g. device rotation)
    const el = tabsRef.current;
    if (el) {
      const ro = new ResizeObserver(() => updateTabsScroll());
      ro.observe(el);
      return () => {
        ro.disconnect();
        document.title = 'Heseda — Skincare Consultant';
      };
    }
    return () => { document.title = 'Heseda — Skincare Consultant'; };
  }, [updateTabsScroll]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      if (!currentUser) {
        navigate('/');
        return;
      }

      // Check admin access
      const adminAccess = checkIsAdmin(currentUser.email);
      setIsAdmin(adminAccess);
      
      if (!adminAccess) {
        alert('Access denied. Admin privileges required.');
        navigate('/chat');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async (): Promise<void> => {
    try {
      try { localStorage.removeItem('heseda-ai-language-explicit'); } catch { /* */ }
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#6c757d'
      }}>
        Loading admin panel...
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header__content">
          <div className="admin-header__left">
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🎛️ Admin Dashboard</h1>
            <div style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '4px' }}>
              Logged in as: {user?.email}
            </div>
          </div>
          <div className="admin-header__right">
            <button
              onClick={() => navigate('/chat')}
              className="admin-button admin-button--secondary"
            >
              👤 Customer View
            </button>
            <button
              onClick={handleLogout}
              className="admin-button admin-button--danger"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className={`admin-tabs-wrapper ${tabsScrollClass}`}>
      <nav className="admin-tabs" ref={tabsRef} onScroll={updateTabsScroll}>
        <button
          className={`admin-tab ${activeTab === 'customers' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          👥 Customers
        </button>
        <button
          className={`admin-tab ${activeTab === 'analytics' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📊 Analytics
        </button>
        <button
          className={`admin-tab ${activeTab === 'sponsored' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('sponsored')}
        >
          💎 Sponsored
        </button>
        <button
          className={`admin-tab ${activeTab === 'feedback' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          📬 Feedback
        </button>
        <button
          className={`admin-tab ${activeTab === 'emailLogs' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('emailLogs')}
        >
          📧 Email Logs
        </button>
        <button
          className={`admin-tab ${activeTab === 'settings' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </nav>
      </div>

      {/* Main Content */}
      <main className="admin-content">
        {activeTab === 'customers' && <CustomersTab language={language} />}
        
        {activeTab === 'analytics' && (
          <div className="analytics-tab-content">
            <nav className="analytics-sub-tabs" role="tablist" aria-label="Analytics sub-tabs">
              <button
                role="tab"
                aria-selected={analyticsSubTab === 'dashboard'}
                className={`analytics-sub-tab ${analyticsSubTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setAnalyticsSubTab('dashboard')}
              >
                📈 Dashboard
              </button>
              <button
                role="tab"
                aria-selected={analyticsSubTab === 'engagement'}
                className={`analytics-sub-tab ${analyticsSubTab === 'engagement' ? 'active' : ''}`}
                onClick={() => setAnalyticsSubTab('engagement')}
              >
                👥 Engagement
              </button>
              <button
                role="tab"
                aria-selected={analyticsSubTab === 'conversion'}
                className={`analytics-sub-tab ${analyticsSubTab === 'conversion' ? 'active' : ''}`}
                onClick={() => setAnalyticsSubTab('conversion')}
              >
                🔄 Conversion
              </button>
              <button
                role="tab"
                aria-selected={analyticsSubTab === 'affiliate'}
                className={`analytics-sub-tab ${analyticsSubTab === 'affiliate' ? 'active' : ''}`}
                onClick={() => setAnalyticsSubTab('affiliate')}
              >
                🔗 Affiliate
              </button>
              <button
                role="tab"
                aria-selected={analyticsSubTab === 'retention'}
                className={`analytics-sub-tab ${analyticsSubTab === 'retention' ? 'active' : ''}`}
                onClick={() => setAnalyticsSubTab('retention')}
              >
                🔄 Retention
              </button>
            </nav>

            {analyticsSubTab !== 'conversion' && (
              <>
                <BusinessAnalytics
                  activeView={
                    analyticsSubTab === 'dashboard'
                      ? 'overview'
                      : analyticsSubTab === 'engagement'
                        ? 'engagement'
                        : analyticsSubTab === 'affiliate'
                          ? 'affiliate'
                          : 'retention'
                  }
                  dateRange={analyticsDateRange}
                  onDateRangeChange={setAnalyticsDateRange}
                />
                {analyticsSubTab === 'dashboard' && <CustomerStageAnalytics />}
                {analyticsSubTab === 'engagement' && <BehaviorInsights />}
              </>
            )}

            {analyticsSubTab === 'conversion' && (
              <ConversionFunnelAnalytics />
            )}
          </div>
        )}

        {activeTab === 'sponsored' && <SponsoredSlotsTab language={language} />}

        {activeTab === 'feedback' && <FeedbackTab language={language} />}

        {activeTab === 'emailLogs' && <EmailLogsTab language={language} />}

        {activeTab === 'settings' && <SettingsTab language={language} />}
      </main>
    </div>
  );
};

export default AdminPage;
